import { useState } from 'react';
import { api } from '../../../api/client';
import { useAction } from '../../../api/hooks';
import { Button } from '../../../ui/Button';
import { Alert, ErrorAlert } from '../../../ui/feedback';
import { Field, Input, Textarea } from '../../../ui/forms';
import { Modal } from '../../../ui/Modal';
import type { ImplDetail } from './types';

export function RecoverDialog({ i, onClose, inv }: { i: ImplDetail; onClose: () => void; inv: unknown[][] }) {
  const [choice, setChoice] = useState<'manual' | 'todo' | 'pending'>('manual');
  const [assessment, setAssessment] = useState('');
  const [reason, setReason] = useState('');
  const m = useAction(
    () =>
      api.post(`/projects/${i.project.key}/tickets/${i.ticket.number}/implementation/recover`, {
        expectedVersion: i.ticket.version,
        choice,
        workspaceAssessment: assessment,
        reason,
      }),
    { success: 'Claim recovered', invalidate: inv as never, onSuccess: onClose },
  );
  const options: [typeof choice, string, string][] = [
    [
      'manual',
      'Take manual ownership',
      `You own ${i.ticket.key} in In progress; it is no longer AI-claimable.`,
    ],
    [
      'todo',
      'Return to Todo',
      'Scope is unchanged; any eligible builder can claim it again with a new generation.',
    ],
    ['pending', 'Return to Pending', 'Use if scope changed; fresh acceptance is required.'],
  ];
  return (
    <Modal
      title={`Recover ${i.displayId}`}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => m.mutate(undefined)}
            loading={m.isPending}
            disabled={!assessment.trim() || !reason.trim()}
          >
            Recover claim
          </Button>
        </>
      }
    >
      <Alert tone="warning">
        Recovery fences the old generation inside the application. It does not stop an external process;
        confirm the workspace state first.
      </Alert>
      <div className="stack sm" role="radiogroup" aria-label="Recovery choice">
        {options.map(([v, label, d]) => (
          <label
            key={v}
            className="check"
            style={{
              border: `1px solid ${choice === v ? 'var(--color-primary)' : 'var(--color-hairline)'}`,
              borderRadius: 8,
              padding: 12,
            }}
          >
            <input type="radio" name="recover" checked={choice === v} onChange={() => setChoice(v)} />
            <span className="stack" style={{ gap: 2 }}>
              <span className="strong">{label}</span>
              <span className="small muted">{d}</span>
            </span>
          </label>
        ))}
      </div>
      <Field
        label="External workspace assessment"
        hint="What you found on the builder: processes, branch state, uncommitted work."
      >
        {(p) => (
          <Textarea {...p} rows={3} value={assessment} onChange={(e) => setAssessment(e.target.value)} />
        )}
      </Field>
      <Field label="Reason">
        {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}
